/**
 * Voice interaction hook and context
 *
 * Pipeline (v0.6.0 — VoicePipeline from @omote/core handles all orchestration):
 * 1. User audio → VAD (speech boundary) + SenseVoice progressive transcription
 * 2. Text → onResponse callback → AgentCore (Strands + Bedrock) → TTS audio
 * 3. Audio → PlaybackPipeline → synchronized audio + A2E lip sync + ExpressionProfile
 *
 * This file only handles:
 * - React context splitting (VoiceFrameContext + VoiceStateContext)
 * - AWS Cognito auth + AgentCore invocation (the onResponse callback)
 * - Zustand transcript store wiring
 */

import { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect, type RefObject, ReactNode } from 'react'
import { BedrockAgentCoreClient, InvokeAgentRuntimeCommand } from '@aws-sdk/client-bedrock-agentcore'
import { CognitoIdentityClient, GetIdCommand, GetOpenIdTokenCommand } from '@aws-sdk/client-cognito-identity'
import { STSClient, AssumeRoleWithWebIdentityCommand } from '@aws-sdk/client-sts'
import type { AwsCredentialIdentity, AwsCredentialIdentityProvider } from '@smithy/types'
import { getSystemInstructions } from '../config/personality'
import { createLogger, VoicePipeline, DEFAULT_MODEL_URLS, isMobile, type VoicePipelineState, type LoadingProgress, type TranscriptResult, type FullFaceFrame, type ConversationalState } from '@omote/core'
import { useTranscriptStore } from './useTranscriptStore'

const logger = createLogger('Voice')

// ============================================================================
// Types
// ============================================================================

export type VoiceState = VoicePipelineState

/** High-frequency frame data (~30fps during AI speech) — only Avatar3D needs this.
 *  Uses refs instead of state to avoid 30 React re-renders/sec during speech.
 *  Avatar3D reads .current inside R3F's useFrame (runs every render frame anyway). */
interface VoiceFrameContextType {
  currentFrameRef: RefObject<Float32Array | null>
  rawFrameRef: RefObject<Float32Array | null>
  currentEmotion: string | null
  stateRef: RefObject<ConversationalState>
  isSpeakingRef: RefObject<boolean>
}

/** Voice state + callbacks (NO transcript data — that lives in Zustand to avoid Canvas re-renders) */
interface VoiceStateContextType {
  state: VoiceState
  error: string | null
  loadingProgress: LoadingProgress | null
  startConversation: () => Promise<void>
  endConversation: () => void
  loadModels: () => Promise<void>
}

const VoiceFrameContext = createContext<VoiceFrameContextType | null>(null)
const VoiceStateContext = createContext<VoiceStateContextType | null>(null)

/** Frame data only (currentFrameRef, currentEmotion) — use in Avatar3D */
export function useVoiceFrame() {
  const context = useContext(VoiceFrameContext)
  if (!context) throw new Error('useVoiceFrame must be used within VoiceProvider')
  return context
}

/** State/transcript/callbacks — use in IntroPanel, TranscriptOverlay */
export function useVoiceState() {
  const context = useContext(VoiceStateContext)
  if (!context) throw new Error('useVoiceState must be used within VoiceProvider')
  return context
}

/** Convenience: merges frame + state contexts. Does NOT include transcript (use useTranscriptStore). */
export function useVoice() {
  return { ...useVoiceFrame(), ...useVoiceState() }
}

// Re-export for backward compat
export type { LoadingProgress }

// ============================================================================
// AWS Cognito + AgentCore (consumer-specific backend)
// ============================================================================

interface RegionConfig {
  region: string
  runtimeArn: string
  identityPoolId: string
  unauthRoleArn: string
}

const REGION_CONFIGS: RegionConfig[] = [
  {
    region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    runtimeArn: import.meta.env.VITE_AGENT_RUNTIME_ARN || '',
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || '',
    unauthRoleArn: import.meta.env.VITE_COGNITO_UNAUTH_ROLE_ARN || '',
  },
  ...(import.meta.env.VITE_AGENT_RUNTIME_ARN_WEST ? [{
    region: 'us-west-2',
    runtimeArn: import.meta.env.VITE_AGENT_RUNTIME_ARN_WEST,
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID_WEST || '',
    unauthRoleArn: import.meta.env.VITE_COGNITO_UNAUTH_ROLE_ARN_WEST || '',
  }] : []),
].filter(r => r.runtimeArn && r.identityPoolId && r.unauthRoleArn)

const IDENTITY_ID_KEY = 'omote-cognito-identity-id'
const WARMUP_SESSION_ID = '__warmup__'
let capturedIdentityId = localStorage.getItem(IDENTITY_ID_KEY) || ''

function cognitoBasicFlowCredentials(config: {
  identityPoolId: string
  unauthRoleArn: string
  region: string
}): AwsCredentialIdentityProvider {
  let cached: AwsCredentialIdentity | null = null

  return async () => {
    if (cached?.expiration && cached.expiration.getTime() > Date.now() + 5 * 60 * 1000) {
      return cached
    }

    const cognitoClient = new CognitoIdentityClient({ region: config.region })

    let IdentityId = localStorage.getItem(IDENTITY_ID_KEY)
    if (!IdentityId) {
      const resp = await cognitoClient.send(new GetIdCommand({ IdentityPoolId: config.identityPoolId }))
      IdentityId = resp.IdentityId || null
      if (IdentityId) localStorage.setItem(IDENTITY_ID_KEY, IdentityId)
    }
    capturedIdentityId = IdentityId || ''

    let Token: string | undefined
    try {
      const resp = await cognitoClient.send(new GetOpenIdTokenCommand({ IdentityId: IdentityId! }))
      Token = resp.Token!
    } catch {
      localStorage.removeItem(IDENTITY_ID_KEY)
      const fresh = await cognitoClient.send(new GetIdCommand({ IdentityPoolId: config.identityPoolId }))
      IdentityId = fresh.IdentityId || null
      if (IdentityId) localStorage.setItem(IDENTITY_ID_KEY, IdentityId)
      capturedIdentityId = IdentityId || ''
      const resp = await cognitoClient.send(new GetOpenIdTokenCommand({ IdentityId: IdentityId! }))
      Token = resp.Token!
    }

    const stsClient = new STSClient({ region: config.region })
    const { Credentials } = await stsClient.send(new AssumeRoleWithWebIdentityCommand({
      RoleArn: config.unauthRoleArn,
      WebIdentityToken: Token!,
      RoleSessionName: 'VoiceAgent',
    }))

    cached = {
      accessKeyId: Credentials!.AccessKeyId!,
      secretAccessKey: Credentials!.SecretAccessKey!,
      sessionToken: Credentials!.SessionToken!,
      expiration: Credentials!.Expiration!,
    }
    return cached
  }
}

async function selectBestRegion(): Promise<RegionConfig> {
  if (REGION_CONFIGS.length <= 1) return REGION_CONFIGS[0]

  const cached = sessionStorage.getItem('voice-agent-region')
  if (cached) {
    const match = REGION_CONFIGS.find(r => r.region === cached)
    if (match) return match
  }

  const results = await Promise.allSettled(
    REGION_CONFIGS.map(async (config) => {
      const start = performance.now()
      try { await fetch(`https://cognito-identity.${config.region}.amazonaws.com/`, { method: 'HEAD', mode: 'no-cors' }) } catch { /* opaque */ }
      return { config, latency: performance.now() - start }
    }),
  )

  const fulfilled = results
    .filter((r): r is PromiseFulfilledResult<{ config: RegionConfig; latency: number }> => r.status === 'fulfilled')
    .sort((a, b) => a.value.latency - b.value.latency)

  const winner = fulfilled[0]?.value.config || REGION_CONFIGS[0]
  sessionStorage.setItem('voice-agent-region', winner.region)
  logger.info(`Selected region: ${winner.region}`)
  return winner
}

const bestRegionPromise = selectBestRegion()

/**
 * Warm up AgentCore: Cognito auth + Lambda cold start.
 * Call after SDK models finish loading (fire-and-forget).
 * Populates clientRef/regionRef so the first real onResponse reuses the warm client.
 */
async function warmUpAgent(
  clientRef: React.MutableRefObject<BedrockAgentCoreClient | null>,
  regionRef: React.MutableRefObject<RegionConfig | null>,
): Promise<void> {
  try {
    const region = await bestRegionPromise
    regionRef.current = region

    const credentials = cognitoBasicFlowCredentials({
      identityPoolId: region.identityPoolId,
      unauthRoleArn: region.unauthRoleArn,
      region: region.region,
    })

    const client = new BedrockAgentCoreClient({
      region: region.region,
      credentials,
    })
    clientRef.current = client

    // Force Cognito credential resolution (GetId + GetOpenIdToken + AssumeRole)
    await credentials()

    // Fire a warmup invocation to force Lambda cold start
    const result = await client.send(
      new InvokeAgentRuntimeCommand({
        agentRuntimeArn: region.runtimeArn,
        payload: new TextEncoder().encode(JSON.stringify({
          type: 'warmup',
          ...(capturedIdentityId ? { actorId: capturedIdentityId } : {}),
        })),
        contentType: 'application/json',
        qualifier: 'DEFAULT',
        runtimeSessionId: WARMUP_SESSION_ID,
      }),
    )

    // Drain the response stream (we don't need the output)
    const stream = result.response as ReadableStream<Uint8Array> | undefined
    if (stream) {
      const reader = stream.getReader()
      try {
        while (!(await reader.read()).done) { /* drain */ }
      } finally {
        reader.releaseLock()
      }
    }

    logger.info('Agent warm-up complete', { region: region.region })
  } catch (err) {
    // Non-fatal — first real request will still work, just with cold start
    logger.warn('Agent warm-up failed (non-fatal)', { error: String(err) })
  }
}

// ============================================================================
// VoiceProvider
// ============================================================================

interface VoiceProviderProps {
  children: ReactNode
}

export function VoiceProvider({ children }: VoiceProviderProps) {
  const [state, setState] = useState<VoiceState>('idle')
  const [error, setError] = useState<string | null>(null)
  const currentFrameRef = useRef<Float32Array | null>(null)
  const rawFrameRef = useRef<Float32Array | null>(null)
  const stateRef = useRef<ConversationalState>('idle')
  const isSpeakingRef = useRef(false)
  const [currentEmotion, setCurrentEmotion] = useState<string | null>(null)
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress | null>(null)

  const setTranscript = useTranscriptStore((s) => s.setTranscript)
  const setInterimTranscript = useTranscriptStore((s) => s.setInterimTranscript)
  const setTranscriptStatus = useTranscriptStore((s) => s.setTranscriptStatus)
  const resetTranscript = useTranscriptStore((s) => s.resetTranscript)

  const pipelineRef = useRef<VoicePipeline | null>(null)
  const clientRef = useRef<BedrockAgentCoreClient | null>(null)
  const regionRef = useRef<RegionConfig | null>(null)

  const loadModels = useCallback(async () => {
    if (pipelineRef.current) return // Already loaded

    setError(null)
    try {
      const pipeline = new VoicePipeline({
        mode: 'cloud',
        models: {
          senseVoice: { modelUrl: DEFAULT_MODEL_URLS.senseVoice, language: 'en' },
          lam: {
            modelUrl: DEFAULT_MODEL_URLS.lam,
          },
          vad: {
            modelUrl: DEFAULT_MODEL_URLS.sileroVad,
            preSpeechBufferChunks: 20,
            // Mobile mic picks up more ambient noise — raise threshold to reduce false positives
            threshold: isMobile() ? 0.65 : 0.5,
          },
        },
        profile: {
          mouth: 1.0,
          jaw: 1.0,
          brows: 0.6,
          eyes: 0.0,
          cheeks: 0.5,
          nose: 0.3,
          tongue: 0.5,
        },
        identityIndex: 0,
        interruptionEnabled: true,
        neutralTransitionEnabled: true,
        normalizeAudio: true,

        // The ONLY consumer-specific code: AgentCore invocation
        onResponse: async ({ text, emotion, event, setEmotion, send, done, signal, sessionId }) => {
          // Lazy-init AgentCore client
          if (!clientRef.current) {
            const region = await bestRegionPromise
            regionRef.current = region
            clientRef.current = new BedrockAgentCoreClient({
              region: region.region,
              credentials: cognitoBasicFlowCredentials({
                identityPoolId: region.identityPoolId,
                unauthRoleArn: region.unauthRoleArn,
                region: region.region,
              }),
            })
          }

          const result = await clientRef.current.send(
            new InvokeAgentRuntimeCommand({
              agentRuntimeArn: regionRef.current!.runtimeArn,
              payload: new TextEncoder().encode(JSON.stringify({
                prompt: text,
                systemPrompt: getSystemInstructions(),
                ...(emotion && emotion !== 'NEUTRAL' && emotion !== 'EMO_UNKNOWN' ? { emotion } : {}),
                ...(event && event !== 'Speech' && event !== 'EVENT_UNKNOWN' ? { event } : {}),
                ...(capturedIdentityId ? { actorId: capturedIdentityId } : {}),
                sessionId,
              })),
              contentType: 'application/json',
              qualifier: 'DEFAULT',
              runtimeSessionId: sessionId,
            }),
            { abortSignal: signal },
          )

          // Parse streaming response (one JSON per line)
          const stream = result.response
          if (!stream) return

          const reader = (stream as ReadableStream<Uint8Array>).getReader()
          const decoder = new TextDecoder()
          let parseBuffer = ''

          try {
            while (true) {
              const { done: isDone, value } = await reader.read()
              if (isDone) break

              parseBuffer += decoder.decode(value, { stream: true })
              const lines = parseBuffer.split('\n')
              parseBuffer = lines.pop() || ''

              for (const line of lines) {
                if (!line.trim()) continue
                let data: any
                try { data = JSON.parse(line) } catch { continue }

                switch (data.type) {
                  case 'response_start':
                    setTranscript(t => ({ ...t, assistant: data.text || '' }))
                    break
                  case 'response_chunk':
                    if (data.text) setTranscript(t => ({ ...t, assistant: t.assistant + data.text }))
                    break
                  case 'audio_chunk':
                    if (data.audio) {
                      const binaryStr = atob(data.audio)
                      const len = binaryStr.length & ~1
                      const bytes = new Uint8Array(len)
                      for (let i = 0; i < len; i++) bytes[i] = binaryStr.charCodeAt(i)
                      await send(bytes)
                    }
                    if (data.isLast) await done()
                    break
                  case 'emotion_update':
                    setCurrentEmotion(data.emotion || null)
                    setEmotion?.(data.emotion)
                    break
                  case 'response_end':
                    if (data.fullText) setTranscript(t => ({ ...t, assistant: data.fullText }))
                    break
                  case 'error':
                    logger.error('Agent error:', data.message)
                    setError(data.message)
                    break
                }
              }
            }
          } finally {
            reader.releaseLock()
          }

          // Process remaining buffer
          if (parseBuffer.trim()) {
            try {
              const data = JSON.parse(parseBuffer)
              if (data.type === 'response_end' && data.fullText) {
                setTranscript(t => ({ ...t, assistant: data.fullText }))
              }
            } catch { /* ignore */ }
          }
        },
      })

      // Wire pipeline events to React state / Zustand store
      pipeline.on('state', (s) => {
        setState(s)
        // Map VoicePipelineState → ConversationalState for life layer
        const stateMap: Record<string, ConversationalState> = {
          idle: 'idle', loading: 'idle', ready: 'idle', error: 'idle',
          listening: 'listening', thinking: 'thinking', speaking: 'speaking',
        }
        stateRef.current = stateMap[s] ?? 'idle'
        isSpeakingRef.current = s === 'speaking'
        if (s === 'listening') setCurrentEmotion(null)
      })

      pipeline.on('loading:progress', setLoadingProgress)

      pipeline.on('frame', (f: FullFaceFrame) => {
        currentFrameRef.current = f.blendshapes
        rawFrameRef.current = f.rawBlendshapes
        if (f.emotion !== undefined) setCurrentEmotion(f.emotion || null)
      })

      pipeline.on('transcript', (t: TranscriptResult) => {
        if (t.isFinal) {
          setTranscript(prev => ({ ...prev, user: t.text }))
          setTranscriptStatus('final')
          setInterimTranscript('')
        } else {
          if (t.text.trim()) {
            setInterimTranscript(t.text.trim())
            setTranscriptStatus('interim')
          }
        }
      })

      pipeline.on('error', (err: Error) => {
        setError(err.message)
      })

      pipeline.on('interruption', () => {
        setCurrentEmotion(null)
        setInterimTranscript('')
        setTranscriptStatus('idle')
      })

      pipelineRef.current = pipeline
      await pipeline.loadModels()
      setLoadingProgress(null)

      // Fire-and-forget: warm up AgentCore (Cognito auth + Lambda cold start)
      // By the time the user finishes their first sentence, the agent is warm
      warmUpAgent(clientRef, regionRef)
    } catch (err) {
      logger.error('Model load error', { error: String(err) })
      setError(err instanceof Error ? err.message : 'Failed to load models')
      // Dispose failed pipeline so retry can create a fresh one
      try { pipelineRef.current?.dispose() } catch { /* ignore */ }
      pipelineRef.current = null
    }
  }, [])

  const startConversation = useCallback(async () => {
    const pipeline = pipelineRef.current
    if (!pipeline || state !== 'ready') return

    resetTranscript()
    setError(null)
    await pipeline.start()
  }, [state])

  const endConversation = useCallback(() => {
    pipelineRef.current?.stop()
    setInterimTranscript('')
    setTranscriptStatus('idle')
    setCurrentEmotion(null)
    currentFrameRef.current = new Float32Array(52)
    rawFrameRef.current = null
  }, [])

  // Cleanup on unmount: stop + dispose pipeline to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pipelineRef.current) {
        try { pipelineRef.current.stop(); } catch { /* ignore */ }
        try { pipelineRef.current.dispose(); } catch { /* ignore */ }
        pipelineRef.current = null;
      }
    };
  }, []);

  const frameValue: VoiceFrameContextType = useMemo(() => ({
    currentFrameRef,
    rawFrameRef,
    currentEmotion,
    stateRef,
    isSpeakingRef,
  }), [currentEmotion])

  const stateValue: VoiceStateContextType = useMemo(() => ({
    state,
    error,
    loadingProgress,
    startConversation,
    endConversation,
    loadModels,
  }), [state, error, loadingProgress, startConversation, endConversation, loadModels])

  return (
    <VoiceFrameContext.Provider value={frameValue}>
      <VoiceStateContext.Provider value={stateValue}>
        {children}
      </VoiceStateContext.Provider>
    </VoiceFrameContext.Provider>
  )
}
